import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:focus-visible:ring-link/50 dark:focus-visible:border-link aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-surface dark:border-border dark:text-foreground dark:hover:bg-accent",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent",
        link: "text-primary underline-offset-4 hover:underline dark:text-link",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/** @type {import("react").ForwardRefExoticComponent<import("react").ComponentPropsWithoutRef<"button"> & import("class-variance-authority").VariantProps<typeof buttonVariants> & { asChild?: boolean } & import("react").RefAttributes<HTMLButtonElement>>} */
const Button = React.forwardRef(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : "button"

  // 🚨 Runtime warning if someone does <Button asChild><button>...</button></Button>
  React.useEffect(() => {
    if (asChild && React.isValidElement(children) && children.type === "button") {
      console.warn(
        "[Button] Do not use <button> as a child when `asChild` is true. This causes nested <button> elements, which is invalid HTML."
      )
    }
  }, [asChild, children])

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </Comp>
  )
})

export { Button, buttonVariants }
